// src/pages/Category.jsx
import React from "react";
import { useParams, Link } from "react-router-dom";

/* ================= CATEGORY IMAGES ================= */
const categoryImages = {
  supplements: "/assets/wellness.jpeg",
  beauty: "/assets/222.jpeg",
  lifestyle: "/assets/333.jpeg",
  books: "/assets/444.jpeg",
};

/* ================= CATEGORY INFO ================= */
const categoryInfo = {
  supplements: {
    title: "Supplements",
    description:
      "Carefully selected supplements that support hormone balance, energy, and wellness.",
  },
  beauty: {
    title: "Beauty & Wellness",
    description:
      "Curated beauty and wellness products for glowing skin, hair, and overall self-care.",
  },
  lifestyle: {
    title: "Lifestyle",
    description:
      "Daily habits and tools to support healthy routines and balanced living.",
  },
  books: {
    title: "Books & Guides",
    description:
      "Educational resources to understand hormones, lifestyle balance, and wellness.",
  },
};

/* ================= ALL PRODUCTS ================= */
const allProducts = [
 // SUPPLEMENTS
{
  id: 1,
  title: "Vitamin D",
  image: "/assets/VITAMIND.webp",
  category: "supplements",
  description: "Supports hormone balance, immune health, and overall wellbeing.",
  link: "https://amzn.to/49JTRkq",
},
{
  id: 2,
  title: "Omega-3 Fish Oil",
  image: "/assets/OMEGA.jpg",
  category: "supplements",
  description: "Helps reduce inflammation and supports heart, brain, and hormone health.",
  link: "https://amzn.to/45XSLjN",
},
{
  id: 3,
  title: "Magnesium",
  image: "/assets/MG.jpeg",
  category: "supplements",
  description: "Supports relaxation, better sleep, muscle function, and stress balance.",
  link: "https://amzn.to/45kvqZu",
},
{
  id: 4,
  title: "Daily Multivitamins",
  image: "/assets/MV.jpeg",
  category: "supplements",
  description: "Provides essential vitamins and minerals to support daily energy and wellness.",
  link: "https://amzn.to/3LW5blx",
},
{
  id: 5,
  title: "Spearmint Tea",
  image: "/assets/ST.jpeg",
  category: "supplements",
  description: "Naturally supports hormone balance and may help reduce excess androgen levels.",
  link: "https://amzn.to/4jYSSBK",
},


  // BEAUTY & WELLNESS
  {
    id: 3,
    title: "Glow Skin Serum",
    image: "/assets/serum.webp",
    category: "beauty",
    description: "Nourishes skin for a radiant glow.",
    link: "https://amzn.to/4qAvjBJ",
  },
  {
    id: 4,
    title: "Relaxing Bath Salts",
    image: "/assets/bath salt.webp",
    category: "beauty",
    description: "Supports relaxation and self-care rituals.",
    link: "https://amzn.to/4a3q33s",
  },

  // LIFESTYLE
  {
    id: 5,
    title: "Yoga Mat",
    image: "/assets/yoga.jpeg",
    category: "lifestyle",
    description: "Comfortable mat for daily exercises and stretches.",
    link: "https://amzn.to/45Rl03G",
  },
  {
    id: 6,
    title: "Mindfulness Journal",
    image: "/assets/jornals.jpeg",
    category: "lifestyle",
    description: "Track habits, moods, and daily reflections.",
    link: "https://amzn.to/4jNGpkh",
  },

  // BOOKS
  {
    id: 7,
    title: "Hormone Balance Guide",
    image: "/assets/444.jpeg",
    category: "books",
    description: "Understand hormones and lifestyle balance.",
    link: "https://amzn.to/4643Rnr",
  },
  {
    id: 8,
    title: "PCOS Wellness Handbook",
    image: "/assets/jor.webp",
    category: "books",
    description: "Practical tips for living well with PCOS.",
    link: "https://amzn.to/49Nw42W",
  },
];

/* ================= CATEGORY COMPONENT ================= */
export default function Category() {
  const { categoryId } = useParams();
  const isAllProducts = !categoryId || categoryId === "all";

  const currentCategory = isAllProducts
    ? {
        title: "All Products",
        description: "Browse all wellness, lifestyle, and educational products.",
      }
    : categoryInfo[categoryId];

  // Filter products by category
  const productsToShow = isAllProducts
    ? allProducts
    : allProducts.filter((p) => p.category === categoryId);

  /* ================= CATEGORY BOXES FOR ALL PRODUCTS ================= */
  const categoryBoxes = Object.keys(categoryInfo).map((key) => (
    <Link
      key={key}
      to={`/category/${key}`}
      className="relative rounded-2xl overflow-hidden w-full sm:w-1/2 md:w-1/4 group hover:scale-105 transition transform"
    >
      <img
        src={categoryImages[key]}
        alt={categoryInfo[key].title}
        className="w-full h-48 object-cover rounded-2xl"
      />
      <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-2xl opacity-0 group-hover:opacity-100 transition">
        <h3 className="text-white font-bold text-lg text-center px-2">
          {categoryInfo[key].title}
        </h3>
      </div>
    </Link>
  ));

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 font-sans">

      {/* ================= HERO ================= */}
      <div
        className={`rounded-3xl mb-16 bg-cover bg-center py-20 px-6 text-center ${
          !isAllProducts ? "text-white" : ""
        }`}
        style={{
          backgroundImage: isAllProducts
            ? "linear-gradient(to bottom, rgba(255,255,255,0.88), rgba(255,255,255,0.95))"
            : `linear-gradient(to bottom, rgba(255,255,255,0.88), rgba(255,255,255,0.95)), url(${categoryImages[categoryId]})`,
        }}
      >
        <h1 className="text-4xl md:text-5xl font-extrabold text-pink-600 mb-4">
          {currentCategory.title}
        </h1>
        <p className="text-gray-700 max-w-2xl mx-auto text-lg">
          {currentCategory.description}
        </p>
      </div>

      {/* ================= CATEGORY NAV ================= */}
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {Object.keys(categoryInfo).map((key) => (
          <Link
            key={key}
            to={`/category/${key}`}
            className={`px-6 py-2 rounded-full border text-sm font-semibold transition ${
              categoryId === key
                ? "bg-pink-500 text-white border-pink-500"
                : "border-gray-300 text-gray-600 hover:bg-pink-50"
            }`}
          >
            {categoryInfo[key].title}
          </Link>
        ))}
      </div>

      {/* ================= ALL PRODUCTS BOXES (ONLY FOR ALL PAGE) ================= */}
      {isAllProducts && (
        <div className="flex flex-wrap gap-6 mb-12 justify-center">
          {categoryBoxes}
        </div>
      )}
{/* ================= CATEGORY PRODUCTS ================= */}
{!isAllProducts && (
  <div className="flex flex-wrap justify-center gap-8">
    {productsToShow.map((product) => (
      <div
        key={product.id}
        className="bg-white rounded-2xl shadow hover:shadow-lg transition overflow-hidden flex flex-col"
        style={{ width: "300px" }}
      >
        {/* Product Image */}
        <img
          src={product.image}
          alt={product.title}
          className="w-full h-[200px] object-cover"
        />

        {/* Product Content */}
        <div className="p-4 flex flex-col flex-grow">
          <h3 className="font-semibold text-lg text-gray-800 mb-2">
            {product.title}
          </h3>

          <p className="text-gray-600 text-sm mb-4">
            {product.description}
          </p>

          <a
            href={product.link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto bg-pink-500 text-white px-6 py-2 rounded-full font-semibold text-center hover:bg-pink-600 transition"
          >
            Shop Now →
          </a>
        </div>
      </div>
    ))}
  </div>

      )}
    </div>
  );
}
